import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../api";
import { LayoutDashboard, Package, Store, ScanLine, CreditCard, LogOut, ExternalLink, Loader2, Tag, Boxes, Zap, UserCog, Users, Gift, CalendarDays, Gem, Smartphone, Banknote, Code2, Building2 } from "lucide-react";
import SalesTab from "./SalesTab";
import ProductsTab from "./ProductsTab";
import IssuedVouchersTab from "./IssuedVouchersTab";
import StorefrontTab from "./StorefrontTab";
import RedeemTab from "./RedeemTab";
import StripeTab from "./StripeTab";
import DiscountsTab from "./DiscountsTab";
import BulkTab from "./BulkTab";
import AccountTab from "./AccountTab";
import CustomersTab from "./CustomersTab";
import ReferralsTab from "./ReferralsTab";
import CalendarTab from "./CalendarTab";
import PlansTab from "./PlansTab";
import TeamTab from "./TeamTab";
import SellTab from "./SellTab";
import WidgetTab from "./WidgetTab";
import CorporateTab from "./CorporateTab";
import { PlanProvider } from "../../context/PlanContext";
import { UpgradeLock } from "../../components/UpgradeLock";
import { TrialBanner } from "../../components/TrialBanner";

function OnboardingNudge({ onGo }) {
  const [state, setState] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/business/connect/status"), api.get("/business/subscription/status")])
      .then(([c, s]) => setState({ connected: c.data.connected, sub: s.data.monthly_fee <= 0 || ["active", "waived", "invoiced"].includes(s.data.status), fee: s.data.monthly_fee, plan: s.data.plan_name }))
      .catch(() => {});
  }, []);
  if (!state || (state.connected && state.sub)) return null;
  const msgs = [];
  if (!state.connected) msgs.push("connect your Stripe account to receive payouts");
  if (!state.sub) msgs.push(`activate your ${state.plan} plan (£${Number(state.fee).toFixed(2)}/mo)`);
  return (
    <div className="mb-6 rounded-2xl bg-gradient-to-r from-[#16243b] to-[#20344f] text-white p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="onboarding-nudge">
      <div className="flex items-center gap-3">
        <Zap className="text-[#2ec9c9] shrink-0" size={22} />
        <div>
          <p className="font-semibold">Finish setting up payments</p>
          <p className="text-sm text-white/70">Please {msgs.join(" and ")} to start selling.</p>
        </div>
      </div>
      <button onClick={onGo} data-testid="nudge-cta" className="gp-btn-teal rounded-xl px-5 py-2.5 text-sm font-medium whitespace-nowrap">Go to Payments</button>
    </div>
  );
}

const tabs = [
  { id: "sales", label: "Overview", icon: LayoutDashboard },
  { id: "products", label: "Vouchers", icon: Package },
  { id: "issued", label: "Sold vouchers", icon: ScanLine },
  { id: "customers", label: "Customers", icon: Users },
  { id: "discounts", label: "Discounts", icon: Tag },
  { id: "bulk", label: "Bulk issue", icon: Boxes },
  { id: "storefront", label: "Storefront", icon: Store },
  { id: "redeem", label: "Redeem", icon: ScanLine },
  { id: "sell", label: "Sell in store", icon: Banknote },
  { id: "team", label: "Staff app", icon: Smartphone },
  { id: "corporate", label: "Corporate orders", icon: Building2 },
  { id: "widget", label: "Website button", icon: Code2 },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "calendar", label: "Calendar", icon: CalendarDays },
  { id: "referrals", label: "Refer & save", icon: Gift },
  { id: "plans", label: "Plans", icon: Gem },
  { id: "account", label: "My account", icon: UserCog },
];

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "sales");

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={40} /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "super_admin") return <Navigate to="/admin" replace />;

  const biz = user.business;

  return (
    <PlanProvider goPlans={() => setTab("plans")}>
    <div className="min-h-screen bg-[#f6f8fb] flex">
      {/* Sidebar */}
      <aside className="w-[240px] bg-[#16243b] text-white flex flex-col fixed h-screen">
        <div className="p-6 flex items-center gap-2">
          <span className="text-xl font-bold tracking-tight">GiftVouchers.app</span>
        </div>
        <nav className="flex-1 px-3 space-y-1 mt-2">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`dash-tab-${t.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                tab === t.id ? "bg-[#2ec9c9] text-white" : "text-[#9fb0c8] hover:bg-white/5 hover:text-white"}`}>
              <t.icon size={18} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          {biz && (
            <a href={`/store/${biz.slug}`} target="_blank" rel="noreferrer"
              className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-[#9fb0c8] hover:text-white transition-colors">
              <ExternalLink size={16} /> View store
            </a>
          )}
          <button onClick={() => { logout(); nav("/login"); }}
            className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-[#9fb0c8] hover:text-white transition-colors">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 ml-[240px] p-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-[#16243b]">{tabs.find((t) => t.id === tab).label}</h1>
          <p className="text-[#5a6b80] text-sm mt-1">{biz?.name}</p>
        </div>
        {tab !== "plans" && <TrialBanner />}
        {tab !== "payments" && <OnboardingNudge onGo={() => setTab("payments")} />}
        {tab === "sales" && <SalesTab />}
        {tab === "issued" && <IssuedVouchersTab />}
        {tab === "customers" && <CustomersTab />}
        {tab === "products" && <ProductsTab />}
        {tab === "discounts" && <DiscountsTab />}
        {tab === "bulk" && <BulkTab />}
        {tab === "storefront" && <StorefrontTab />}
        {tab === "redeem" && <RedeemTab />}
        {tab === "payments" && <StripeTab />}
        {tab === "account" && <AccountTab />}
        {tab === "referrals" && <ReferralsTab />}
        {tab === "calendar" && <UpgradeLock feature="calendar"><CalendarTab /></UpgradeLock>}
        {tab === "plans" && <PlansTab />}
        {tab === "sell" && <UpgradeLock feature="in_store_sales"><SellTab /></UpgradeLock>}
        {tab === "team" && <UpgradeLock feature="staff_app"><TeamTab /></UpgradeLock>}
        {tab === "corporate" && <UpgradeLock feature="corporate_portal"><CorporateTab /></UpgradeLock>}
        {tab === "widget" && <UpgradeLock feature="website_widget"><WidgetTab /></UpgradeLock>}
      </main>
    </div>
    </PlanProvider>
  );
}
